import { ActionIcon, Burger, Center, Group, Stack, Text, Tooltip } from '@mantine/core';
import { IconHelp } from '@tabler/icons-react';
import { UserProfile } from './UserProfile';
import { ThemeToggle } from '@/ui/components/ThemeToggle';
import { CadwellLogoIcon } from "@/assets/CadwellIcons/CadwellLogo.tsx";
import { useFormattedUser, useLogoutRedirect } from '@/features/auth/hooks/auth.hooks';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { SearchBarWithSuggestions } from '@/features/search/components/SearchBarWithSuggestions';
import { useMediaQuery } from "@mantine/hooks";
import { themeConfig } from "@/ui/theme/theme.config.ts";

interface AppHeaderProps {
  mobileOpened: boolean;
  toggleMobile: () => void;
}

export const AppHeader = ({mobileOpened, toggleMobile}: AppHeaderProps) => {
  const formattedUser = useFormattedUser();
  const logout = useLogoutRedirect();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(`(max-width: ${themeConfig.breakpoints?.md})`);
  const isHomeOrSearchPage = location.pathname === '/' || location.pathname === '/search';

  const handleLogoClick = () => {
    navigate({to: '/'});
  };

  return (
    <Group p='md' justify="space-between" align="center" wrap="nowrap">
      {/* Left side - Mobile burger, Logo and title */}
      <Group gap="md" wrap="nowrap">
        <Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom='md' size="sm"/>
        <Center py={6} pl={10}>
          <CadwellLogoIcon size={120}/>
        </Center>
        <Stack gap={0}>
          <Text
            fw={600}
            style={{cursor: 'pointer'}}
            onClick={handleLogoClick}
            aria-label="navigate-to-home"
          >
            CadCare Connect
          </Text>
        </Stack>
      </Group>

      {/* Right side - Theme Toggle and User Profile */}
      <Group gap="md" justify="right" flex={1} align="center" wrap="nowrap">
        {/* Center - Search bar (not on home page) */}
        {!isHomeOrSearchPage && !isMobile && (
          <SearchBarWithSuggestions size="sm"/>
        )}
        <Tooltip label="Help" visibleFrom="md">
          <ActionIcon
            component="a"
            href="/help/"
            target="_blank"
            rel="noopener noreferrer"
            variant="subtle"
            aria-label="help-documentation"
            visibleFrom="md"
          >
            <IconHelp size={20}/>
          </ActionIcon>
        </Tooltip>
        <ThemeToggle showOnMobile={false}/>
        <UserProfile
          user={formattedUser}
          onLogout={logout}
          showOnMobile={false}
        />
      </Group>
    </Group>
  );
};
